import { useEffect } from 'react';

import { useStatsigClient } from '@coursera-web/statsig';

import {
  ORG_SCOPED_HOME_OVERRIDE_ORG_IDS,
  ORG_SCOPED_HOME_STATSIG_GATE,
} from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useUserExperienceMemberships } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import { useThirdPartyOrganization as useOrgScopedThirdPartyOrg } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

/**
 * Determines if the org-scoped unified home experience is enabled.
 *
 * Pre-rollout: returns true only when the user's current org or any program membership org
 * is in ORG_SCOPED_HOME_OVERRIDE_ORG_IDS. Used for early-access partners and testing orgs.
 *
 * TODO(HOME-5213): On rollout day, add back the `enableOrgScopedHome` product feature check
 * so all opted-in orgs are included: `isEnabled = isOverrideOrg || featureEnabled`.
 */
export function useIsOrgScopedHome(): boolean {
  const { checkGate } = useStatsigClient();
  const naptimeThirdPartyOrg = useThirdPartyOrganization();
  const orgScopedThirdPartyOrg = useOrgScopedThirdPartyOrg();
  const { programMemberships } = useUserExperienceMemberships();

  const thirdPartyOrgId = orgScopedThirdPartyOrg?.id ?? naptimeThirdPartyOrg?.id;

  const isOverrideOrg =
    (!!thirdPartyOrgId && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(thirdPartyOrgId)) ||
    (programMemberships?.some((edge) => {
      const orgId = edge?.node?.program?.naptimeThirdPartyOrganization?.id;
      return orgId != null && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(orgId);
    }) ??
      false);

  const isEnabled = isOverrideOrg;

  // Log Statsig impression when org-scoped home is active (for analytics tracking).
  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
